import axios from "axios";
import { getStringFromError } from "../functions/errorGetter";
import {
  useProfileStore,
  useSessionStore,
  useTempSessionStore,
} from "../stores/UserStore";
import { jwtDecode } from "jwt-decode";
import { notifications } from "@mantine/notifications";
import QueryString from "qs";
import { modals } from "@mantine/modals";
import ErrorModal from "../layouts/ErrorModal/ErrorModal";
import MaintenancePage from "../pages/unprotected/errors/MaintenancePage";
import { setCookie } from "../functions/utilFunctions";

export const api_url =
  localStorage.getItem("api_url") && localStorage.getItem("api_url") !== "null"
    ? localStorage.getItem("api_url")
    : window._env_.API_URL || "https://app.api-magileads.net";

export const mainAxios = axios.create({
  baseURL: api_url,
  paramsSerializer: (params) =>
    QueryString.stringify(params, { arrayFormat: "repeat" }),
  headers: {
    "Content-Type": "application/json",
  },
});

export const authChannel = new BroadcastChannel("auth_channel");
export const switchAuthChannel = new BroadcastChannel("switch_auth_channel");

async function handleMainRefresh() {
  const session = JSON.parse(localStorage.getItem("session"))?.state.session;

  try {
    const { data } = await mainAxios.post(
      "/users/authentication/refresh",
      { refresh_token: session?.refresh_token },
      { headers: { "Is-Refresh": true } } // Bypasses the interceptor
    );

    // Create the new session object from the server's response
    const new_session = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };



    const tokenData = jwtDecode(data.access_token);
    // tokenData.iat is in seconds, Date.now() is in ms
    const serverTimeMs = tokenData.iat * 1000;
    const clientTimeMs = Date.now();
    const clockSkew = serverTimeMs - clientTimeMs;
    localStorage.setItem("clock_skew", clockSkew.toString());

    // Update the current tab's state immediately
    useSessionStore.getState().setSession(new_session);

    // Broadcast the successful refresh so other tabs can update their state
    authChannel.postMessage({ type: "REFRESH_SUCCESS", payload: new_session });

    return new_session;
  } catch (error) {
    // Check if the error is from a token already being used by another tab
    if (error.response?.data?.state_message === "refresh_token_already_used") {
      // This is an expected failure for a tab that "lost" the race.
      console.warn("Refresh token already used; another tab likely succeeded.");
    } else {
      // This is a REAL, unexpected failure (e.g., token truly invalid).
      authChannel.postMessage({ type: "REFRESH_FAILURE" });
    }
    return Promise.reject(error);
  }
}

async function handleSwitchRefresh(user_switch) {
  const session = JSON.parse(localStorage.getItem("session"))?.state.session;

  try {
    const { data } = await mainAxios.post(
      `/users/switch/to/${user_switch?.id}`,
      undefined,
      {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          "Is-Refresh-Switch": true, // Flag to bypass interceptor loop
        },
      }
    );

    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + 50); // Set new expiration

    const new_user_switch = {
      ...user_switch,
      token: data.token,
      exp: expiresAt.getTime(),
    };

    // 1. Update the current tab's state
    useSessionStore.getState().setUserSwitch(new_user_switch);

    // 2. Broadcast the success to other tabs
    switchAuthChannel.postMessage({
      type: "SWITCH_REFRESH_SUCCESS",
      payload: new_user_switch,
    });

    return new_user_switch;
  } catch (error) {
    if (error.response?.data?.state_message === "cannot_switch_to_this_user") {
      switchAuthChannel.postMessage({ type: "SWITCH_REFRESH_FAILURE" });
    } else {
      console.warn(
        "Switch refresh failed, likely due to a temporary state or race condition.",
        error
      );
    }

    // Handle failure for the current tab
    useSessionStore.getState().setUserSwitch(null);
    notifications.show({
      title: "Switched Session Ended",
      message: "Could not renew the session for the switched user.",
      color: "orange",
    });

    return Promise.reject(error);
  }
}

//#region Request Interceptor
mainAxios.interceptors.request.use(
  async (config) => {
    // Don't intercept the refresh requests themselves
    if (config.headers["Is-Refresh"] || config.headers["Is-Refresh-Switch"] || config.headers["Skip-Auth"]) {
      return config;
    }
    let session = useSessionStore.getState().session;
    let user_switch = useSessionStore.getState().user_switch;

    // Handle standalone API Key requests
    const urlParams = new URLSearchParams(window.location.search);
    const apiKey = urlParams.get("apiKey") || localStorage.getItem("api_key");
    const allowSwitch =
      urlParams.get("allowSwitch") || localStorage.getItem("allow_switch");

    if (apiKey?.length > 10) {
      config.headers["X-API-Key"] = apiKey;
      setCookie("api_key", apiKey);

      if (!user_switch || allowSwitch !== "true") {
        return config;
      }
    } else {
      setCookie("api_key", "");
    }

    // If not logged in, proceed without a token
    if (!session?.access_token && !user_switch && apiKey?.length > 10) {
      return config;
    }

    // --- 2. Calculate Expiration Status ---
    const clockSkew = parseInt(localStorage.getItem("clock_skew") || "0", 10);
    // Apply the skew to the client's current time to get the real server time
    const trueServerTime = Date.now() + clockSkew;

    const isMainTokenExpired = config?._forceRefresh || (session?.access_token
      ? trueServerTime >= jwtDecode(session?.access_token)?.exp * 1000 - 60000
      : false);      

    const isSwitchTokenExpired = user_switch?.exp
      ? trueServerTime >= user_switch.exp - 60000
      : false;

    // --- 3. Handle Main Token Refresh ---

    if (isMainTokenExpired) {
      // Use the Web Locks API to ensure only ONE tab performs the refresh.
      await navigator.locks.request("main_token_refresh_lock", async (lock) => {
        const latestSession = useSessionStore.getState().session;

        // BULLETPROOF CONCURRENCY CHECK:
        // If the token changed while we were waiting for the lock, 
        // it means another request already did the refresh for us!
        if (session.access_token !== latestSession.access_token) {
          return; // Exit early, nothing to do
        }

        // Only rely on the clock check if we ARE NOT forcing a refresh
        if (!config._forceRefresh) {
          const stillExpired = Date.now() >= jwtDecode(latestSession.access_token).exp * 1000 - 60000;
          if (!stillExpired) return;
        }

        try {
          await handleMainRefresh();
        } catch (error) {
          if (
            error.response?.data?.state_message !== "refresh_token_already_used"
          ) {
            throw error;
          }
        }
      });
      // CRITICAL: Update local variable after lock releases
      session = useSessionStore.getState().session;
      
      // CLEANUP: Remove the flag so it doesn't get stuck
      delete config._forceRefresh;
    }

    // --- 4. Handle Switched User Token Refresh ---

    if (user_switch && isSwitchTokenExpired) {
      await navigator.locks.request(
        "switch_token_refresh_lock",
        async (lock) => {
          const latestUserSwitch = useSessionStore.getState().user_switch;
          const stillExpired = latestUserSwitch?.exp
            ? Date.now() >= latestUserSwitch.exp - 60000
            : false;

          if (!stillExpired) return;

          try {
            await handleSwitchRefresh(latestUserSwitch);
          } catch (error) {
            if (
              error.response?.data?.state_message !==
              "refresh_token_already_used"
            ) {
              throw error;
            }
          }
        }
      );
      // CRITICAL: Update local variable after lock releases
      user_switch = useSessionStore.getState().user_switch;
    }

    // --- 5. Attach Final Tokens to Headers ---
    if (session?.access_token) {
      // Correctly overwrites any old token from the retried config
      config.headers.Authorization = `Bearer ${session.access_token}`;
    } else if (apiKey?.length > 10) {
      config.headers["X-API-Key"] = apiKey;
    }

    if (user_switch && !config.headers["Is-Main"]) {
      config.headers["X-API-Key"] = user_switch.token;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
//#endregion

//#region Response Interceptor
async function handleErrorDisplay(error) {
  if (error?.message === "Request aborted" || axios.isCancel(error)) {
    return;
  }
  if (!error?.response?.data || error?.message === "Network Error") {
    return notifications.show({ message: "Network error", color: "red" });
  }

  if (
    error?.response?.data?.state_message?.includes("reached") ||
    error?.response?.data?.state_message?.includes(
      "unauthorized_permission_value"
    )
  ) {
    return modals.open({
      modalId: "error-modal",
      centered: true,
      children: <ErrorModal message={error.response.data.state_message} />,
      size: "lg",
    });
  }

  const error_message = await getStringFromError(error);
  notifications.show({ message: error_message, color: "red" });
}

const responseErrorInterceptor = async (error) => {
  const originalRequest = error.config;

  // Handle Maintenance Mode
  if (
    error.response?.data?.state_message === "in_maintenance" ||
    error.response?.status === 503
  ) {
    return modals.open({
      modalId: "maintenance-modal",
      children: <MaintenancePage />,
      fullScreen: true,
      withCloseButton: false,
      zIndex: 9999999,
    });
  }

  // --- RECOVERABLE ERROR: Token Expired (Sleep Gap Fix) ---
  // If the server says token_expired, it means our preemptive check failed (likely due to sleep).
  // We simply retry the request. The Request Interceptor will run again, see Date.now() is
  // totally expired, and trigger the refresh logic automatically.
  if (
    error.response?.data?.state_message === "token_expired" &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true; // Prevent infinite loops
    originalRequest._forceRefresh = true;
    return mainAxios(originalRequest);
  }

  // --- FATAL ERRORS ---
  // Handle critical authentication errors that require logout
  const criticalAuthErrors = [
    "refresh_token_expired",
    "refresh_token_already_used",
    "refresh_token_does_not_exist",
    "token_not_exist",
    "user_account_disabled",
    "you_must_re_authenticate",
    "token_empty",
    "too_many_attempts",
    "token_expired",
  ];

  if (criticalAuthErrors.includes(error.response?.data?.state_message)) {
    useSessionStore.getState().setUserSwitch(null);
    useSessionStore.getState().setSession(null);
    useProfileStore.getState().setProfile(null);
    console.log("failure occured:" + error.response?.data?.state_message);
    return window.location.replace(
      `/login?destination=${window.location.pathname !== "/login" ? window.location.pathname : "/"
      }&reason=${error.response?.data?.state_message}`
    );
  }

  // For all other errors, display a notification
  handleErrorDisplay(error);

  return Promise.reject(error);
};

mainAxios.interceptors.response.use(
  (response) => {
    const user_switch = useSessionStore.getState().user_switch;
    if (!user_switch) useTempSessionStore.getState().setIsReloaded(false);
    return response;
  },
  (error) => responseErrorInterceptor(error)
);
//#endregion